import {
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail,
  sendEmailVerification,
  applyActionCode,
  signOut,
  deleteUser,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';

export interface FirebaseUserData {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  userType: 'FOOD_BANK' | 'SUPPLIER' | 'VOLUNTEER';
  profileImage?: string;
  isVerified: boolean;
  onboardingCompleted?: boolean;
  createdAt: string;
  backendUserId?: string; // Link to your backend user ID
}

export interface LoginResult {
  success: boolean;
  user?: FirebaseUserData;
  error?: string;
}

export interface RegisterResult {
  success: boolean;
  user?: FirebaseUserData;
  error?: string;
}

class FirebaseAuthService {
  private currentUser: User | null = null;
  private userData: FirebaseUserData | null = null;
  private tokenRefreshBlockedUntil = 0;

  constructor() {
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      if (!user) {
        this.userData = null;
        localStorage.removeItem('firebaseToken');
      }
    });
  }

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<LoginResult> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const idToken = await user.getIdToken();
      localStorage.setItem('firebaseToken', idToken);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  // Register new user
  async register(email: string, password: string, userData: Omit<FirebaseUserData, 'uid' | 'email' | 'createdAt' | 'backendUserId'>): Promise<RegisterResult> {
    // Prevent UserContext firebase-sync from creating a default VOLUNTEER row mid-registration
    sessionStorage.setItem('registrationInProgress', userData.userType)
    try {
      console.log('🔵 Creating Firebase user...', { userType: userData.userType })
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Get Firebase ID token
      const idToken = await user.getIdToken();
      localStorage.setItem('firebaseToken', idToken);
      console.log('✅ Firebase user created, token stored')

      // Create user in your backend with retry logic
      console.log('🔵 Creating backend user...')
      let backendUser = null;
      let retries = 3;
      let backendError: Error | null = null;
      
      while (retries > 0 && !backendUser) {
        try {
          backendUser = await this.createBackendUser({
            uid: user.uid,
            email: user.email!,
            createdAt: new Date().toISOString(),
            ...userData
          });
          
          if (backendUser) {
            console.log('✅ Backend user created successfully')
            break;
          }
        } catch (error: any) {
          backendError = error instanceof Error ? error : new Error(String(error));
          retries--;
          console.log(`⚠️ Backend user creation failed, retries left: ${retries}`)

          // Validation/conflict errors (such as a duplicate phone) cannot succeed
          // on retry. Stop immediately instead of flooding the API forever.
          if (!error?.retryable) {
            break;
          }

          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      // If backend user creation failed, do not continue with a partially-created account.
      if (!backendUser) {
        try {
          await deleteUser(user)
        } catch (cleanupError) {
          console.warn('⚠️ Failed to clean up Firebase user after backend registration failure:', cleanupError)
        }
        localStorage.removeItem('firebaseToken')
        return {
          success: false,
          error: backendError?.message || 'We could not complete account setup. Please try again.'
        }
      }

      // Send email verification once after registration (verify page should not auto-resend)
      if (!user.emailVerified) {
        const verifyResult = await this.sendEmailVerification();
        if (verifyResult.success) {
          sessionStorage.setItem('verificationEmailSent', 'true');
          console.log('✅ Verification email sent to', user.email);
        } else {
          console.warn('⚠️ Failed to send verification email:', verifyResult.error);
        }
      }

      const newUserData: FirebaseUserData = {
        uid: user.uid,
        email: user.email!,
        createdAt: new Date().toISOString(),
        backendUserId: backendUser?.id,
        ...userData
      };

      this.userData = {
        ...newUserData,
        userType: backendUser.userType ?? userData.userType,
      };
      return { success: true, user: this.userData };
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      return { success: false, error: this.getErrorMessage(error) };
    } finally {
      sessionStorage.removeItem('registrationInProgress');
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      await signOut(auth);
      this.userData = null;
      localStorage.removeItem('firebaseToken');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Send password reset email (Firebase)
  async sendPasswordReset(
    email: string,
    continueUrl?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const normalizedEmail = (email || '').trim()
      if (!normalizedEmail) return { success: false, error: 'Please enter an email address' }

      // Including a continue URL avoids Firebase rejecting the request on some configurations
      // (e.g., unauthorized-continue-uri) and produces a better user experience.
      const actionCodeSettings = continueUrl
        ? { url: continueUrl, handleCodeInApp: false }
        : undefined

      await sendPasswordResetEmail(auth, normalizedEmail, actionCodeSettings as any)
      return { success: true }
    } catch (error: any) {
      return { success: false, error: this.getErrorMessage(error) }
    }
  }

  /** Continue URL after Firebase processes the email action (must be an Authorized domain). */
  private getEmailVerificationContinueUrl(): string | undefined {
    const configured =
      typeof import.meta !== 'undefined' ? import.meta.env?.VITE_AUTH_CONTINUE_URL : undefined
    if (configured && typeof configured === 'string' && configured.trim()) {
      const base = configured.trim().replace(/\/$/, '')
      return base.includes('/verify-email') ? base : `${base}/verify-email`
    }

    if (typeof window === 'undefined') return undefined
    const host = window.location.hostname
    // Prefer apex production URL so www redirect / preview hosts don't break allowlisting
    if (host === 'harvesthelp.app' || host === 'www.harvesthelp.app') {
      return 'https://harvesthelp.app/verify-email'
    }
    if (host === 'localhost' || host === '127.0.0.1') {
      return `${window.location.origin}/verify-email`
    }
    return undefined
  }

  // Send email verification. Call only from signup or the verify-email resend button — never login.
  async sendEmailVerification(): Promise<LoginResult> {
    try {
      const currentUser = auth.currentUser || this.currentUser;
      if (!currentUser) {
        return { success: false, error: 'No user is currently signed in' };
      }

      if (currentUser.emailVerified) {
        return { success: true, error: 'Email is already verified' };
      }

      const continueUrl = this.getEmailVerificationContinueUrl();
      try {
        if (continueUrl) {
          await sendEmailVerification(currentUser, {
            url: continueUrl,
            handleCodeInApp: false,
          });
        } else {
          await sendEmailVerification(currentUser);
        }
      } catch (err: any) {
        // Custom continue URL rejected → one retry without it (do not loop)
        if (
          continueUrl &&
          (err?.code === 'auth/unauthorized-continue-uri' ||
            err?.code === 'auth/invalid-continue-uri')
        ) {
          console.warn(
            'Continue URL not allowlisted in Firebase; retrying without custom redirect. ' +
              'Add harvesthelp.app under Firebase Auth → Settings → Authorized domains.'
          );
          await sendEmailVerification(currentUser);
        } else {
          throw err;
        }
      }

      return { success: true };
    } catch (error: any) {
      console.error('Email verification error:', error);
      return { success: false, error: this.getErrorMessage(error) };
    }
  }

  /** Apply the oobCode from a verification email link (works even if signed out). */
  async applyEmailVerificationCode(oobCode: string): Promise<LoginResult> {
    try {
      if (!oobCode) {
        return { success: false, error: 'Missing verification code from the email link.' }
      }
      await applyActionCode(auth, oobCode)
      const currentUser = auth.currentUser || this.currentUser
      if (currentUser) {
        await currentUser.reload()
        await this.syncEmailVerificationStatus()
      }
      return { success: true }
    } catch (error: any) {
      console.error('Apply email verification code error:', error)
      // Link already used but user may already be verified
      if (error?.code === 'auth/invalid-action-code') {
        const currentUser = auth.currentUser || this.currentUser
        if (currentUser) {
          await currentUser.reload()
          if (currentUser.emailVerified) {
            await this.syncEmailVerificationStatus()
            return { success: true }
          }
        }
        return {
          success: false,
          error:
            'This verification link has expired or was already used. Request a new email and open the newest link (check Spam/Junk).'
        }
      }
      return { success: false, error: this.getErrorMessage(error) }
    }
  }

  // Check and sync Firebase email verification status to backend
  async syncEmailVerificationStatus(): Promise<boolean> {
    try {
      const currentUser = auth.currentUser || this.currentUser;
      if (!currentUser) return false;

      // Reload user to get latest emailVerified status from Firebase
      await currentUser.reload();
      
      // Sync to backend if email is verified
      if (currentUser.emailVerified) {
        const firebaseToken = await this.getFirebaseToken();
        if (!firebaseToken) return false;

        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/verification/email/sync`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${firebaseToken}`
            }
          });

          if (response.ok) {
            // Refresh user data from backend
            await this.loadUserDataFromBackend(currentUser.uid);
            return true;
          }
        } catch (error) {
          console.error('Failed to sync email verification status:', error);
        }
      }

      return false;
    } catch (error) {
      console.error('Error syncing email verification:', error);
      return false;
    }
  }

  getCurrentUser(): FirebaseUserData | null {
    return this.userData;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    // Check Firebase auth directly first (most reliable)
    const firebaseUser = auth.currentUser || this.currentUser;
    return !!firebaseUser;
  }

  async getFirebaseToken(forceRefresh: boolean = false): Promise<string | null> {
    try {
      const currentUser = auth.currentUser || this.currentUser;
      if (!currentUser) {
        return localStorage.getItem('firebaseToken');
      }

      const quotaBlocked = Date.now() < this.tokenRefreshBlockedUntil;
      if (quotaBlocked) {
        return localStorage.getItem('firebaseToken');
      }

      const token = await currentUser.getIdToken(forceRefresh);
      localStorage.setItem('firebaseToken', token);
      this.currentUser = currentUser;
      return token;
    } catch (error: any) {
      const code = error?.code || '';
      if (code === 'auth/quota-exceeded' || code === 'auth/too-many-requests') {
        this.tokenRefreshBlockedUntil = Date.now() + 60_000;
        console.warn('Firebase token quota exceeded; using cached token if available');
        return localStorage.getItem('firebaseToken');
      }
      console.error('❌ Error getting Firebase token:', code || error?.message || error);
      return localStorage.getItem('firebaseToken');
    }
  }

  // Load user data from backend (public so UserContext can refresh it)
  async loadUserDataFromBackend(uid: string): Promise<FirebaseUserData | null> {
    try {
      const firebaseToken = await this.getFirebaseToken();
      if (!firebaseToken) {
        console.warn('⚠️ No Firebase token available for loading user data');
        return null;
      }

      // Try to get user profile from backend
      const response = await fetch(`${API_BASE_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${firebaseToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          const userData: FirebaseUserData = {
            uid: uid,
            email: data.data.email,
            firstName: data.data.firstName,
            lastName: data.data.lastName,
            phone: data.data.phone,
            userType: data.data.userType,
            profileImage: data.data.profileImage,
            isVerified: data.data.isVerified,
            onboardingCompleted: data.data.onboardingCompleted,
            createdAt: data.data.createdAt,
            backendUserId: data.data.id
          };
          
          this.userData = userData;
          console.log('✅ User data loaded from backend:', userData.email, 'userType:', userData.userType);
          return userData;
        }
      } else {
        // Check if it's a "user not found" error (needs sync)
        try {
          const errorData = await response.json();
          if (errorData.needsSync || errorData.error?.includes('not found in database')) {
            console.log('ℹ️ User not found in database, sync will be triggered by UserContext');
            // Return null so UserContext can trigger sync
            return null;
          }
        } catch (e) {
          // Response is not JSON, log the status
          console.warn(`⚠️ Profile fetch failed with status ${response.status}`);
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error loading user data from backend:', error);
      return null;
    }
  }

  // Create user in backend
  private async createBackendUser(userData: FirebaseUserData): Promise<any> {
    const firebaseToken = await this.getFirebaseToken();
    if (!firebaseToken) {
      const error: any = new Error('Authentication token could not be created. Please try again.');
      error.retryable = true;
      throw error;
    }

    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/api/auth/firebase-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${firebaseToken}`
        },
        body: JSON.stringify({
          firebaseUid: userData.uid,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          // Phone is collected during onboarding; omit placeholders
          ...(userData.phone && !userData.phone.startsWith('__pending__')
            ? { phone: userData.phone }
            : {}),
          userType: userData.userType
        })
      });
    } catch (networkError) {
      console.error('Backend user creation network error:', networkError);
      const error: any = new Error('Could not reach the server. Please try again.');
      error.retryable = true;
      throw error;
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      console.log('✅ Backend register response userType:', data.data?.userType);
      return data.data;
    }

    console.error('Backend user creation failed:', response.status, data);
    const error: any = new Error(
      data.error || (response.status >= 500 ? 'The server could not create your account.' : 'Account registration failed.')
    );
    error.status = response.status;
    error.retryable = response.status === 429 || response.status >= 500;
    throw error;
  }

  // Get user-friendly error message
  private getErrorMessage(error: any): string {
    switch (error.code) {
      case 'auth/user-not-found':
        return 'No account found with this email address';
      case 'auth/wrong-password':
        return 'Incorrect password';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters';
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/too-many-requests':
        return 'Too many attempts. Wait several minutes (sometimes up to an hour), then try Resend again.';
      case 'auth/unauthorized-continue-uri':
        return 'This site domain is not authorized in Firebase Auth. Add harvesthelp.app under Authorized domains.';
      case 'auth/invalid-continue-uri':
        return 'Verification redirect URL is invalid. Please contact support.';
      default:
        return error.message || 'An error occurred during authentication';
    }
  }
}

// Create singleton instance
export const firebaseAuthService = new FirebaseAuthService();
export default firebaseAuthService;